import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Crown, Link2, Clock, RefreshCw, User, Shield, Flag, Swords, History, ShieldCheck, ChevronDown } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Empty, RowsSkeleton } from "../components/UI";

const GROUPS = [
  { key: "INDIVIDUAL", label: "Individual", Icon: User },
  { key: "CLUB", label: "Clubs", Icon: Shield },
  { key: "NATIONAL_TEAM", label: "National Teams", Icon: Flag },
  { key: "MATCHES", label: "Matches", Icon: Swords },
];
const WRAP = "mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12";
const UNIT_SUFFIX = {
  odds: "Winning coupon odds", wins: "ArenaBook duel wins", pts: "Points",
  duels: "Consecutive duels", rating: "Peak rating", snapshots: "#1 ranking snapshots",
  titles: "Titles", caps: "Caps", matches: "Consecutive matches", margin: "Team-point margin",
};
const HOLDER_ICON = { PLAYER: User, CLUB: Shield, NATIONAL_TEAM: Flag };

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return null; }
}
function fmtDateTime(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return null; }
}

export default function HallOfFamePage() {
  const { hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("INDIVIDUAL");
  const isAdmin = hasRole && hasRole("WSBF_ADMIN", "FEDERATION_ADMIN");

  const load = useCallback(() => {
    api.get("/records").then(({ data }) => setData(data)).catch(() => setData({ groups: {}, records: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const recompute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/records/recompute");
      toast.success(`Recomputed ${data.computed} records from source data`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <RowsSkeleton rows={6} />;
  const total = data.records?.length || 0;
  const available = GROUPS.filter((g) => (data.groups?.[g.key] || []).length > 0);
  const activeTab = available.some((g) => g.key === tab) ? tab : (available[0]?.key || "INDIVIDUAL");
  const items = data.groups?.[activeTab] || [];

  return (
    <div className="animate-fade-up pb-24 overflow-x-hidden">
      {/* ---- MONUMENTAL HERO ---- */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.12] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 130% at 50% -20%, rgba(212,175,55,0.20) 0%, transparent 58%)" }} />
        <div className="absolute left-1/2 -translate-x-1/2 top-0 opacity-[0.08] pointer-events-none" aria-hidden="true"
          style={{ width: 900, height: 360, background: "conic-gradient(from 180deg at 50% 0%, transparent 70deg, #E5B842 90deg, transparent 110deg)" }} />
        <div className={`${WRAP} relative py-12 sm:py-16`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.32em] text-[#E5B842] font-mono font-bold mb-3 flex items-center gap-2">
                <Trophy className="w-3.5 h-3.5" /> WSTF · All-Time Records
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-slate-50 leading-none wsbf-slant-block">Hall of Fame</h1>
              <p className="mt-4 max-w-2xl text-sm text-slate-400 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-[#E5B842] shrink-0 mt-0.5" />
                <span>Every record is computed from authoritative match, duel, rating and honour data — <b className="text-slate-200 font-semibold">never hand-entered</b>. Each holder links to its source.</span>
              </p>
            </div>
            {isAdmin && (
              <button data-testid="record-recompute" onClick={recompute} disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#161616] border border-[#2b2512] text-slate-200 hover:border-[#E5B842]/50 disabled:opacity-40"
                style={{ transition: "border-color 0.15s ease" }}>
                <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Recomputing…" : "Recompute"}
              </button>
            )}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-wider text-slate-400">
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#E5B842]" /> Last recomputed <b className="text-slate-200">{fmtDateTime(data.computed_at) || "—"}</b></span>
            <span className="text-slate-700">·</span>
            <span><b className="text-[#E5B842]">{total}</b> records live</span>
          </div>
        </div>
      </section>

      <div className={`${WRAP} pt-8`}>
        {total === 0 ? <Empty label="No records computed yet." /> : (
          <>
            {/* ---- CATEGORY NAVIGATION ---- */}
            <div className="flex gap-1 border-b border-[#2b2512] mb-8 overflow-x-auto no-scrollbar" data-testid="record-category-nav">
              {available.map(({ key, label, Icon }) => (
                <button key={key} data-testid={`record-tab-${key}`} onClick={() => setTab(key)}
                  className={`px-4 py-2.5 text-sm font-heading font-bold uppercase tracking-wide border-b-2 whitespace-nowrap inline-flex items-center gap-2 ${activeTab === key ? "border-[#E5B842] text-[#E5B842]" : "border-transparent text-slate-400 hover:text-slate-200"}`}
                  style={{ transition: "color 0.15s ease" }}>
                  <Icon className="w-4 h-4" /> {label}
                  <span className="text-[10px] font-mono text-slate-600">{(data.groups?.[key] || []).length}</span>
                </button>
              ))}
            </div>

            <section data-testid={`record-group-${activeTab}`} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((r) => <RecordCard key={r.category} r={r} />)}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function RecordCard({ r }) {
  const h = r.holder || {};
  const since = fmtDate(r.achieved_at);
  const unit = UNIT_SUFFIX[r.unit] || "";
  const HIcon = HOLDER_ICON[h.type] || Crown;
  const titles = r.context?.titles;
  const prov = r.context?.link;
  const [open, setOpen] = useState(false);
  const [hist, setHist] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && hist === null && !loading) {
      setLoading(true);
      try {
        const { data } = await api.get(`/records/${r.category}/history`);
        setHist(data);
      } catch { setHist({ timeline: [], count: 0 }); }
      finally { setLoading(false); }
    }
  };

  return (
    <div data-testid={`record-card-${r.category}`}
      className="group relative overflow-hidden rounded-2xl foil-border bg-gradient-to-b from-[#14110a] to-[#050505] p-6 flex flex-col"
      style={{ transition: "transform 0.2s ease" }}>
      <div className="absolute inset-0 mesh-grid opacity-[0.16] pointer-events-none" />
      <div className="absolute -right-6 -top-6 opacity-[0.06] pointer-events-none" aria-hidden="true"><Trophy className="w-28 h-28 text-[#E5B842]" /></div>
      <div className="relative flex flex-col h-full">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#E5B842] font-mono font-bold mb-4 flex items-center gap-1.5">
          <Trophy className="w-3.5 h-3.5" /> {r.label}
        </div>

        {/* VALUE dominant */}
        <div data-testid={`record-value-${r.category}`} className="font-heading text-6xl font-black text-[#E5B842] tabular-nums leading-none">
          {r.value_str}
        </div>
        {unit && <div className="mt-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{unit}</div>}

        {/* Holder */}
        <div className="mt-5 flex items-center gap-2 min-w-0">
          <HIcon className="w-4 h-4 text-[#E5B842] shrink-0" />
          {h.link ? (
            <Link to={h.link} data-testid={`record-holder-${r.category}`}
              className="font-heading font-bold uppercase text-slate-50 hover:text-[#E5B842] break-words min-w-0" style={{ transition: "color 0.15s ease" }}>
              {h.name}
            </Link>
          ) : (
            <span className="font-heading font-bold uppercase text-slate-50 break-words min-w-0">{h.name}</span>
          )}
          {h.country_iso3 && <span className="text-[10px] font-mono text-slate-500 shrink-0">{h.country_iso3}</span>}
        </div>

        {/* Context */}
        {r.context?.text && (
          <div className="mt-2 text-xs text-slate-400 leading-snug">{r.context.text}</div>
        )}
        {titles && Array.isArray(titles) && (
          <div className="mt-2 text-xs text-slate-400 leading-snug">{titles.join(" · ")}</div>
        )}

        <div className="flex-1" />

        {/* Provenance + since */}
        <div className="mt-4 pt-3 border-t border-[#2b2512] flex items-center justify-between gap-2">
          {since ? <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Since {since}</span> : <span />}
          {prov && (
            <Link to={prov} data-testid={`record-source-${r.category}`}
              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#E5B842] hover:underline">
              <Link2 className="w-3 h-3" /> Source
            </Link>
          )}
        </div>

        {/* VIEW RECORD HISTORY — expandable "how it was broken" timeline */}
        <button data-testid={`record-history-toggle-${r.category}`} onClick={toggle}
          aria-expanded={open}
          className="mt-3 inline-flex items-center justify-between gap-2 w-full px-3 py-2 rounded-lg bg-[#0b0b0b] border border-[#2b2512] text-[11px] font-mono uppercase tracking-wider text-slate-300 hover:border-[#E5B842]/50 hover:text-[#E5B842]"
          style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
          <span className="inline-flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> View record history</span>
          <ChevronDown className={`w-3.5 h-3.5 shrink-0 ${open ? "rotate-180" : ""}`} style={{ transition: "transform 0.2s ease" }} />
        </button>

        {open && (
          <div className="mt-3" data-testid={`record-history-panel-${r.category}`}>
            {loading ? (
              <div className="text-[11px] font-mono text-slate-500 px-1 py-2">Loading history…</div>
            ) : !hist || (hist.timeline || []).length === 0 ? (
              <div data-testid={`record-history-empty-${r.category}`} className="text-[11px] font-mono text-slate-500 leading-relaxed px-1 py-2">
                Set from the first computation and never beaten since — this record has stood unbroken.
              </div>
            ) : (
              <ol className="relative border-l border-[#2b2512] ml-1.5 space-y-4" data-testid={`record-history-timeline-${r.category}`}>
                {hist.timeline.map((s, i) => <HistoryStep key={i} s={s} />)}
              </ol>
            )}
          </div>
        )}

        {/* Previous holder */}
        {r.previous?.holder?.name && (
          <div data-testid={`record-previous-${r.category}`} className="mt-3 text-[10px] font-mono text-slate-600 flex items-center gap-1.5">
            <History className="w-3 h-3" /> Previous: {r.previous.holder.name} ({r.previous.value_str || r.previous.value})
          </div>
        )}
      </div>
    </div>
  );
}

function HistoryStep({ s }) {
  const h = s.holder || {};
  const HIcon = HOLDER_ICON[h.type] || Crown;
  const took = fmtDate(s.took_at);
  return (
    <li className="ml-4 relative">
      <span className={`absolute -left-[22px] top-1 w-3 h-3 rounded-full border-2 ${s.is_current ? "bg-[#E5B842] border-[#E5B842]" : "bg-[#0b0b0b] border-[#4a4228]"}`} aria-hidden="true" />
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="font-heading text-lg font-black text-[#E5B842] tabular-nums leading-none">{s.value_str}</span>
        {s.is_current && <span className="text-[9px] font-mono uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">Current</span>}
      </div>
      <div className="mt-1 flex items-center gap-1.5 min-w-0">
        <HIcon className="w-3.5 h-3.5 text-slate-500 shrink-0" />
        {h.link ? (
          <Link to={h.link} className="text-xs font-semibold text-slate-200 hover:text-[#E5B842] break-words min-w-0" style={{ transition: "color 0.15s ease" }}>{h.name}</Link>
        ) : (
          <span className="text-xs font-semibold text-slate-200 break-words min-w-0">{h.name}</span>
        )}
        {h.country_iso3 && <span className="text-[9px] font-mono text-slate-600 shrink-0">{h.country_iso3}</span>}
      </div>
      <div className="mt-0.5 text-[10px] font-mono uppercase tracking-wider text-slate-600">
        {took ? (s.took_at ? `Took the record · ${took}` : took) : "Earliest on record"}
        {s.advances > 1 && <span className="text-slate-500"> · extended it {s.advances}×</span>}
      </div>
    </li>
  );
}
